/**
 * 调试面板
 * 用于显示调试信息和前端日志
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { isTouchDevice } from '../../../../utils/platformUtils';
import {
  debugPanelVisibleAtom,
  debugPanelPositionAtom,
  debugPanelCollapsedAtom,
  debugLogsAtom,
  useFrontendLogger
} from '../../../../atoms/v3/debugAtoms';

// 自定义滚动条样式
const scrollbarStyles = `
  .debug-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #6b7280 #374151;
  }
  .debug-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  .debug-scrollbar::-webkit-scrollbar-track {
    background: #374151;
    border-radius: 4px;
  }
  .debug-scrollbar::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 4px;
    border: 1px solid #374151;
  }
  .debug-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
  .debug-scrollbar::-webkit-scrollbar-thumb:active {
    background: #d1d5db;
  }
`;

interface DebugPanelProps {
  title?: string;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({
  title = "🔍  Debug Panel"
}) => {
  const [isVisible, setIsVisible] = useAtom(debugPanelVisibleAtom);
  const logs = useAtomValue(debugLogsAtom);
  const [isCollapsed, setIsCollapsed] = useAtom(debugPanelCollapsedAtom);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useAtom(debugPanelPositionAtom);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const logContainerRef = useRef<HTMLDivElement>(null);

  // 前端日志记录器
  const logger = useFrontendLogger();

  // 自动滚动到最新日志
  useEffect(() => {
    if (logs.length > 0) {
      setTimeout(() => {
        if (logContainerRef.current) {
          const container = logContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      }, 10);
    }
  }, [logs]);

  // 拖拽处理 - 使用指针捕获，避免全局事件冲突
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // 只有点击标题栏才能拖拽
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) {
      return; // 不是拖拽手柄，不处理
    }

    // 检查是否点击了按钮，如果是按钮就不拖拽
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return; // 点击了按钮，不处理拖拽
    }

    e.preventDefault();
    e.stopPropagation();

    // 设置指针捕获，确保拖拽流畅
    const element = e.currentTarget as HTMLElement;
    element.setPointerCapture(e.pointerId);

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    e.preventDefault();
    e.stopPropagation();

    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();

      // 释放指针捕获
      const element = e.currentTarget as HTMLElement;
      element.releasePointerCapture(e.pointerId);
    }
    setIsDragging(false);
  }, [isDragging]);

  // 清空日志
  const clearLogs = useCallback(() => {
    logger.clear();
  }, [logger]);

  // 复制所有日志到剪切板
  const copyLogs = useCallback(async () => {
    if (logs.length === 0) {
      return;
    }

    const logText = logs.map(log => {
      let line = `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message}`;
      if (log.data) {
        line += ` | Data: ${JSON.stringify(log.data)}`;
      }
      if (log.stack) {
        line += `\nStack: ${log.stack}`;
      }
      return line;
    }).join('\n');

    try {
      await navigator.clipboard.writeText(logText);
      logger.info('日志已复制到剪切板');
    } catch (err) {
      // 降级方案：创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = logText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      logger.info('日志已复制到剪切板 (降级方案)');
    }
  }, [logs, logger]);

  // 获取设备信息
  const getDeviceInfo = () => {
    const isTouch = isTouchDevice();
    return {
      isTouch,
      maxTouchPoints: navigator.maxTouchPoints,
      hasOntouchstart: 'ontouchstart' in window,
      pointerCoarse: window.matchMedia('(pointer: coarse)').matches,
      userAgent: navigator.userAgent,
      isMobile: navigator.userAgent.includes('Mobile'),
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
      isAndroid: /Android/.test(navigator.userAgent),
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`
    };
  };

  const deviceInfo = getDeviceInfo();

  // 如果不可见，不渲染
  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* 注入自定义滚动条样式 */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

      <div
        className="fixed bg-black/95 backdrop-blur-sm text-white text-xs border border-gray-600 rounded-lg shadow-2xl z-[9999]"
        style={{
          left: position.x,
          top: `max(${position.y}px, calc(env(safe-area-inset-top) + 10px))`, // 确保避让状态栏
          width: isCollapsed ? '200px' : '350px',
          maxWidth: 'calc(100vw - 20px)', // 确保不超出屏幕
          maxHeight: isCollapsed ? '40px' : '80vh',
          pointerEvents: 'auto' // 确保只有调试面板本身接收事件
        }}
      >
      {/* 标题栏 - 只有这里可以拖拽，完全隔离事件 */}
      <div
        data-drag-handle
        className="flex items-center justify-between p-2 bg-gray-800 rounded-t-lg cursor-grab active:cursor-grabbing select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp} // 鼠标离开也结束拖拽
        style={{
          touchAction: 'none', // 禁用触摸行为，让指针捕获生效
          isolation: 'isolate' // 隔离层叠上下文
        }}
      >
        <span className="font-bold text-yellow-300">{title}</span>
        <div className="flex gap-1">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs"
          >
            {isCollapsed ? '▼' : '▲'}
          </button>
          <button
            onClick={copyLogs}
            className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs"
            disabled={logs.length === 0}
          >
            📋
          </button>
          <button
            onClick={clearLogs}
            className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs"
          >
            清空
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="px-2 py-1 bg-gray-600 hover:bg-gray-700 rounded text-xs"
            title="关闭调试面板"
          >
            ✕
          </button>
        </div>
      </div>

      {/* 内容区域 - 可滚动 */}
      {!isCollapsed && (
        <div className="p-3 space-y-3">
          {/* 设备信息 */}
          <div>
            <div className="font-bold text-green-300 mb-1">📱 设备信息</div>
            <div className="space-y-1 text-gray-300">
              <div>isTouch: <span className="text-yellow-300">{deviceInfo.isTouch.toString()}</span></div>
              <div>maxTouchPoints: <span className="text-blue-300">{deviceInfo.maxTouchPoints}</span></div>
              <div>ontouchstart: <span className="text-purple-300">{'ontouchstart' in window ? 'yes' : 'no'}</span></div>
              <div>pointer:coarse: <span className="text-orange-300">{deviceInfo.pointerCoarse ? 'yes' : 'no'}</span></div>
              <div>Mobile: <span className="text-pink-300">{deviceInfo.isMobile ? 'yes' : 'no'}</span></div>
              <div>iOS: <span className="text-cyan-300">{deviceInfo.isIOS ? 'yes' : 'no'}</span></div>
              <div>Android: <span className="text-lime-300">{deviceInfo.isAndroid ? 'yes' : 'no'}</span></div>
              <div>Screen: <span className="text-gray-400">{deviceInfo.screenSize}</span></div>
              <div>Viewport: <span className="text-gray-400">{deviceInfo.viewportSize}</span></div>
            </div>
          </div>

          {/* 日志区域 */}
          <div>
            <div className="font-bold text-blue-300 mb-1">📝 前端日志 ({logs.length})</div>

            {/* 日志滚动区域 */}
            <div
              ref={logContainerRef}
              className="w-full h-[300px] overflow-y-auto bg-gray-900 text-white text-xs leading-relaxed p-2 border border-gray-600 rounded debug-scrollbar"
              style={{
                touchAction: 'pan-y', // 允许垂直滚动
                overscrollBehavior: 'contain' // 防止滚动传播到父元素
              }}
              onPointerDown={(e) => {
                // 阻止日志区域的指针事件传播到拖拽处理器
                e.stopPropagation();
              }}
            >
              {logs.length === 0 ? (
                <div className="text-gray-500 p-5 text-center">
                  暂无日志...
                </div>
              ) : (
                logs.map((log) => (
                  <div
                    key={`log-${log.id}`}
                    className={`p-2 m-1 rounded border-l-2 ${
                      log.level === 'error'
                        ? 'bg-red-900/50 text-red-200 border-red-400'
                        : log.level === 'warn'
                        ? 'bg-yellow-900/50 text-yellow-200 border-yellow-400'
                        : log.level === 'debug'
                        ? 'bg-purple-900/50 text-purple-200 border-purple-400'
                        : 'bg-blue-900/50 text-blue-200 border-blue-400'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-1">
                      <span>{log.level === 'error' ? '❌' : log.level === 'warn' ? '⚠️' : log.level === 'debug' ? '🐛' : 'ℹ️'}</span>
                      <span>{log.timestamp}</span>
                      <span className="uppercase font-bold">{log.level}</span>
                    </div>
                    <div className="break-words mb-1">{log.message}</div>
                    {log.data && (
                      <div className="text-[10px] text-gray-300 bg-gray-800 p-1 rounded mt-1">
                        <strong>Data:</strong> {JSON.stringify(log.data, null, 2)}
                      </div>
                    )}
                    {log.stack && (
                      <div className="text-[10px] text-red-300 bg-red-900/30 p-1 rounded mt-1">
                        <strong>Stack:</strong>
                        <pre className="whitespace-pre-wrap">{log.stack}</pre>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default DebugPanel;
